import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useFocusTheme } from "../theme/FocusThemeContext";

// Caps how long the splash may hold for the first focus response —
// if the API is slow the page reveals anyway and the theme fades in later.
const MAX_HOLD_MS = 900;
// Must stay in sync with the .loading-plate / .splash-title transition
// durations in global.css.
const EXIT_MS = 350;

// The splash leaves by settling its title onto the landing <h1> instead of
// fading out, so the page reads as assembling itself. That only works when the
// hero is actually on screen — every other case falls back to a plain fade.
function measureHandoff(titleEl) {
  if (!titleEl) return null;
  if (window.location.pathname !== "/") return null;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return null;

  const target = document.querySelector(".landing h1");
  if (!target) return null;

  const to = target.getBoundingClientRect();
  if (!to.width || !to.height) return null;
  // useScrollRestoration may have jumped to a saved offset — if the hero isn't
  // in view there is nothing to hand off to.
  if (to.bottom <= 0 || to.top >= window.innerHeight) return null;

  // The entrance animation is still transforming the title, and a transformed
  // box would make the delta wrong. Drop it first: reading the rect below
  // forces the recalc, so we measure the plain layout box. Anything that bails
  // from here on has to put it back, or the title pops mid-entrance.
  titleEl.style.animation = "none";
  const abort = () => {
    titleEl.style.animation = "";
    return null;
  };

  const from = titleEl.getBoundingClientRect();
  if (!from.width) return abort();

  // One uniform scale off the width ratio keeps letter-spacing and line-height
  // proportional, so the last frame is pixel-identical to the hero. That only
  // holds while both boxes have the same shape — if the hero wrapped to two
  // lines and the splash didn't, the heights won't agree and a fade is honest.
  const s = to.width / from.width;
  if (Math.abs(from.height * s - to.height) > 2) return abort();

  return {
    x: to.left + to.width / 2 - (from.left + from.width / 2),
    y: to.top + to.height / 2 - (from.top + from.height / 2),
    s,
  };
}

export default function LoadingScreen() {
  const { ready } = useFocusTheme();
  const [elapsed, setElapsed] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setElapsed(true), MAX_HOLD_MS);
    return () => clearTimeout(t);
  }, []);

  // Inter arrives from the Google Fonts @import. Handing off while it's still
  // swapping would reflow the title mid-flight, so wait for it (MAX_HOLD_MS
  // still caps the wait).
  useEffect(() => {
    if (!document.fonts) {
      setFontsReady(true);
      return;
    }
    let live = true;
    document.fonts.ready.then(() => live && setFontsReady(true));
    return () => {
      live = false;
    };
  }, []);

  // Same documentElement dataset pattern as data-focus — global.css hides the
  // subtitle and nav while the splash is up, then rises them in on "out".
  // Deliberately never cleared: unmounting mid-animation would cut the reveal.
  useEffect(() => {
    document.documentElement.dataset.splash = "in";
  }, []);

  const dismiss = (ready && fontsReady) || elapsed;

  // Layout effect so the measurement and the class flip land in one paint.
  useLayoutEffect(() => {
    if (!dismiss || leaving) return;

    const flip = measureHandoff(titleRef.current);
    if (flip) {
      const el = titleRef.current;
      el.style.setProperty("--flip-x", `${flip.x}px`);
      el.style.setProperty("--flip-y", `${flip.y}px`);
      el.style.setProperty("--flip-s", flip.s);
      // The hero <h1> is the flight's destination and sits right under the
      // fading plate — showing both would read as a double image. It comes
      // back the moment the splash unmounts, in the same frame the title
      // lands on it.
      document.documentElement.dataset.handoff = "1";
    }

    document.documentElement.dataset.splash = "out";
    setLeaving(true);
  }, [dismiss, leaving]);

  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => setGone(true), EXIT_MS);
    return () => clearTimeout(t);
  }, [leaving]);

  useEffect(() => {
    if (!gone) return;
    delete document.documentElement.dataset.handoff;
  }, [gone]);

  if (gone) return null;

  return (
    <div
      className={`loading-screen${leaving ? " loading-screen--leaving" : ""}`}
      role="status"
      aria-label="Loading"
    >
      <div className="loading-plate" />
      <p className="splash-title" ref={titleRef}>
        Oliver Bujdoš
      </p>
    </div>
  );
}
